import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, BookOpen, 
  Wrench, Cog, Building2, Briefcase, GraduationCap, Play,
  Monitor, FileText, Settings, Globe, Shield, TrendingUp, Cloud
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

// NetSuite Certification Training Paths
const trainingPaths = [
  {
    id: 'foundation',
    name: 'NetSuite SuiteFoundation',
    level: 'Beginner',
    duration: '6-8 weeks',
    description: 'Build your NetSuite foundation with comprehensive understanding of the platform, navigation, core functionalities, and standard business processes.',
    color: 'from-sky-500 to-sky-700',
    icon: '🎯',
    topics: [
      'NetSuite Platform Overview',
      'User Interface & Navigation',
      'Records & Transactions',
      'Searching & Reporting',
      'Roles & Permissions',
      'Dashboards & KPIs',
      'Basic Customization',
      'Data Import/Export'
    ],
    outcomes: [
      'Navigate NetSuite confidently',
      'Configure basic settings',
      'Create reports and saved searches',
      'Understand NetSuite architecture',
      'Prepare for SuiteFoundation certification'
    ],
    salary: '$60,000 - $90,000',
    demand: 'Very High',
    industries: ['All Industries', 'SMBs', 'Mid-Market']
  },
  {
    id: 'administrator',
    name: 'NetSuite Administrator',
    level: 'Intermediate',
    duration: '8-10 weeks',
    description: 'Master NetSuite administration including user management, customization, workflows, and system configuration for enterprise deployments.',
    color: 'from-blue-500 to-blue-700',
    icon: '⚙️',
    topics: [
      'User & Role Administration',
      'Custom Records & Fields',
      'SuiteFlow (Workflows)',
      'SuiteScript Basics',
      'Integration Management',
      'Security & Compliance',
      'Performance Optimization',
      'Sandbox Management'
    ],
    outcomes: [
      'Administer NetSuite environments',
      'Create custom workflows',
      'Manage integrations',
      'Optimize system performance',
      'Prepare for Administrator certification'
    ],
    salary: '$75,000 - $115,000',
    demand: 'Very High',
    industries: ['Technology', 'E-commerce', 'Professional Services']
  },
  {
    id: 'erp-consultant',
    name: 'NetSuite ERP Consultant',
    level: 'Intermediate to Advanced',
    duration: '10-12 weeks',
    description: 'Become a certified NetSuite ERP Consultant with expertise in Financials, Inventory, Order Management, and Manufacturing modules.',
    color: 'from-emerald-500 to-emerald-700',
    icon: '💼',
    topics: [
      'General Ledger & Financials',
      'Accounts Payable & Receivable',
      'Revenue Recognition',
      'Inventory Management',
      'Order Management',
      'Procurement',
      'Manufacturing (WIP & Routing)',
      'Multi-Subsidiary Consolidation'
    ],
    outcomes: [
      'Implement NetSuite ERP modules',
      'Configure financial workflows',
      'Set up inventory management',
      'Handle multi-subsidiary setups',
      'Prepare for ERP Consultant certification'
    ],
    salary: '$90,000 - $140,000',
    demand: 'High',
    industries: ['Manufacturing', 'Wholesale', 'Retail', 'Services']
  },
  {
    id: 'suitecloud',
    name: 'NetSuite SuiteCloud Developer',
    level: 'Advanced',
    duration: '12-14 weeks',
    description: 'Master NetSuite development with SuiteScript 2.x, SuiteFlow, SuiteTalk, and SuiteCloud IDE for building custom solutions and integrations.',
    color: 'from-purple-500 to-purple-700',
    icon: '💻',
    topics: [
      'SuiteScript 2.x Development',
      'Client & Server Scripts',
      'RESTlet & Suitelet Development',
      'SuiteTalk Web Services',
      'SuiteCloud IDE',
      'Custom Portlets & Dashboards',
      'Bundle Creation & Deployment',
      'Performance Best Practices'
    ],
    outcomes: [
      'Build custom SuiteScripts',
      'Create integrations with SuiteTalk',
      'Develop custom portlets',
      'Deploy SuiteApps/Bundles',
      'Prepare for SuiteCloud Developer certification'
    ],
    salary: '$100,000 - $160,000',
    demand: 'Very High',
    industries: ['Technology', 'Software', 'SuiteApps Partners']
  }
];

// NetSuite Modules
const netsuiteModules = [
  { id: 'financials', name: 'Financial Management', icon: TrendingUp, duration: '2.5 weeks', topics: ['General Ledger', 'AP/AR', 'Fixed Assets', 'Cash Management', 'Budgeting'] },
  { id: 'inventory', name: 'Inventory & Supply Chain', icon: Building2, duration: '2 weeks', topics: ['Item Management', 'Bin/Lot Control', 'Demand Planning', 'Warehouse', 'Transfers'] },
  { id: 'order-mgmt', name: 'Order Management', icon: Globe, duration: '2 weeks', topics: ['Sales Orders', 'Fulfillment', 'Returns', 'Pricing', 'Shipping'] },
  { id: 'crm', name: 'CRM & Marketing', icon: Users, duration: '1.5 weeks', topics: ['Lead Management', 'Opportunities', 'Campaigns', 'Customer Portal', 'Case Management'] },
  { id: 'manufacturing', name: 'Manufacturing', icon: Cog, duration: '2 weeks', topics: ['Work Orders', 'Assemblies', 'Routing', 'WIP', 'Capacity Planning'] },
  { id: 'analytics', name: 'Analytics & Reporting', icon: Monitor, duration: '1.5 weeks', topics: ['Saved Searches', 'SuiteAnalytics', 'Workbooks', 'Dashboards', 'Financial Reports'] }
];

// Corporate clients
const corporateClients = ['Roku', 'GoPro', 'Ancestry', 'LegalZoom', 'Restoration Hardware', 'Williams-Sonoma'];

// Industries
const industries = [
  { name: 'Software & Technology', icon: Monitor },
  { name: 'Wholesale Distribution', icon: Building2 },
  { name: 'Retail & E-commerce', icon: Globe },
  { name: 'Professional Services', icon: Briefcase },
  { name: 'Manufacturing', icon: Cog },
  { name: 'Non-Profit', icon: Shield }
];

export default function NetSuiteDetailPage() {
  const [selectedPath, setSelectedPath] = useState('foundation');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'NetSuite Demo',
        last_name: 'Request',
        email,
        message: 'Interested in NetSuite ERP Training - Demo Request',
        source: 'netsuite_demo'
      });
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="netsuite-detail-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-sky-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/programs" className="inline-flex items-center text-sm text-sky-600 hover:text-sky-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              
              <Badge className="bg-sky-100 text-sky-700 mb-4">#1 Cloud ERP for Growing Businesses</Badge>
              
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-3xl mb-6">
                <Cloud className="w-8 h-8 text-white" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                NetSuite ERP Training & Certification
              </h1>
              <p className="text-xl text-sky-600 font-medium mb-4">
                Oracle NetSuite Cloud ERP
              </p>
              <p className="text-lg text-slate-600 mb-8">
                NetSuite is the world's leading cloud business suite with 37,000+ customers. 
                Master this powerful ERP system covering Financials, CRM, E-commerce, 
                Inventory, and advanced development with SuiteCloud platform.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/pricing">
                  <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6" data-testid="netsuite-enroll-btn">
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="rounded-full px-6 border-sky-200 text-sky-700 hover:bg-sky-50">
                    <Play className="w-4 h-4 mr-2" /> Free Demo Session
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-sky-600">37K+</p>
                  <p className="text-xs text-slate-500">Customers</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-sky-600">$120K+</p>
                  <p className="text-xs text-slate-500">Avg Salary</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-sky-600">93%</p>
                  <p className="text-xs text-slate-500">Placement Rate</p>
                </div>
              </div>
            </div>
            
            {/* Program Highlights Card */}
            <Card className="shadow-xl border-0 bg-white">
              <CardHeader className="bg-gradient-to-r from-sky-600 to-sky-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Program Highlights
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Flexible Duration</p>
                    <p className="text-sm text-slate-600">6-14 weeks based on track</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Live Online Training</p>
                    <p className="text-sm text-slate-600">Expert-led via Zoom/Teams</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <Cloud className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Hands-On Labs</p>
                    <p className="text-sm text-slate-600">NetSuite sandbox access</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Official Certification Prep</p>
                    <p className="text-sm text-slate-600">NetSuite certification guidance</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-sky-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Job Assistance</p>
                    <p className="text-sm text-slate-600">Resume & interview prep</p>
                  </div>
                </div>
                
                {/* Demo Request Form */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-2">Request Free Demo</p>
                  <form onSubmit={handleDemoRequest} className="flex gap-2">
                    <Input 
                      type="email" 
                      placeholder="Enter your email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      data-testid="netsuite-demo-email"
                    />
                    <Button type="submit" className="bg-sky-600 hover:bg-sky-700" disabled={loading}>
                      {loading ? '...' : 'Go'}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using NetSuite</p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <div key={industry.name} className="flex items-center px-4 py-2 bg-slate-50 rounded-full text-sm">
                <industry.icon className="w-4 h-4 text-sky-600 mr-2" />
                <span className="text-slate-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NetSuite Certification Paths */}
      <section className="py-20 lg:py-28" id="certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-sky-100 text-sky-700 mb-4">Official NetSuite Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your NetSuite Certification Path</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enroll in specific NetSuite certification training aligned with your career goals
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPaths.map((path) => (
              <Card 
                key={path.id} 
                className={`relative overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedPath === path.id ? 'border-sky-500 shadow-xl ring-2 ring-sky-200' : 'border-slate-200 hover:border-sky-300 hover:shadow-lg'
                }`}
                onClick={() => setSelectedPath(path.id)}
                data-testid={`cert-path-${path.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <CardTitle className="text-xl text-slate-900">{path.name}</CardTitle>
                        <p className="text-sm text-slate-500">{path.level} • {path.duration}</p>
                      </div>
                    </div>
                    <Badge className={`${path.demand === 'Very High' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
                      {path.demand} Demand
                    </Badge>
                  </div>
                  <CardDescription className="text-slate-600">{path.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Industries */}
                    <div className="flex flex-wrap gap-2">
                      {path.industries.map((ind, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">{ind}</span>
                      ))}
                    </div>
                    
                    {/* Salary */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-slate-600">Expected Salary:</span>
                      <span className="font-bold text-green-700">{path.salary}</span>
                    </div>
                    
                    {/* Topics */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics:</p>
                      <div className="grid grid-cols-2 gap-1">
                        {path.topics.slice(0, 6).map((topic, i) => (
                          <div key={i} className="flex items-center text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-sky-500 mr-1 flex-shrink-0" />
                            <span className="truncate">{topic}</span>
                          </div>
                        ))}
                      </div>
                      {path.topics.length > 6 && (
                        <p className="text-xs text-sky-600 mt-1">+{path.topics.length - 6} more topics</p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex gap-3 pt-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${path.color} text-white hover:opacity-90`}>
                          Enroll Now
                        </Button>
                      </Link>
                      <Link to="/webinar">
                        <Button variant="outline" className="border-slate-300">
                          <Play className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Curriculum */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">NetSuite Training Curriculum</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive modules covering NetSuite ERP and SuiteCloud platform
            </p>
          </div>
          
          <Tabs defaultValue="financials" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {netsuiteModules.slice(0, 4).map((module) => (
                <TabsTrigger 
                  key={module.id} 
                  value={module.id}
                  className="px-4 py-2 rounded-full border border-slate-200 data-[state=active]:border-sky-500 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700"
                >
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {netsuiteModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="max-w-4xl mx-auto">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                        <module.icon className="w-6 h-6 text-sky-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{module.name}</h3>
                        <p className="text-sky-600 font-medium">Duration: {module.duration}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">Topics Covered</h4>
                        <ul className="space-y-3">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">What You'll Learn</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start space-x-2">
                            <BookOpen className="w-5 h-5 text-sky-500 mt-0.5" />
                            <span className="text-slate-600">Real-time scenario-based training</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Monitor className="w-5 h-5 text-sky-500 mt-0.5" />
                            <span className="text-slate-600">Hands-on lab exercises</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <FileText className="w-5 h-5 text-sky-500 mt-0.5" />
                            <span className="text-slate-600">Study materials & documentation</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Award className="w-5 h-5 text-sky-500 mt-0.5" />
                            <span className="text-slate-600">Certification guidance</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* All Modules Grid */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">All NetSuite Modules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {netsuiteModules.map((module, index) => (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                        <span className="text-sky-700 font-mono font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{module.name}</p>
                        <p className="text-xs text-slate-500">{module.duration}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">NetSuite Certifications We Prepare You For</h2>
              <p className="text-slate-600 mb-6">
                Our training programs prepare you for official Oracle NetSuite certifications.
              </p>
              <ul className="space-y-3">
                {[
                  { cert: 'NetSuite SuiteFoundation Certification', color: 'text-sky-600' },
                  { cert: 'NetSuite Administrator Certification', color: 'text-blue-600' },
                  { cert: 'NetSuite ERP Consultant Certification', color: 'text-emerald-600' },
                  { cert: 'NetSuite SuiteCloud Developer I/II', color: 'text-purple-600' },
                  { cert: 'NetSuite Financial User Certification', color: 'text-orange-600' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className={`w-5 h-5 ${item.color} mt-0.5`} />
                    <span className="text-slate-700 font-medium">{item.cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                NetSuite skills are in high demand. Here are roles you can pursue.
              </p>
              <ul className="space-y-3">
                {[
                  'NetSuite Administrator',
                  'NetSuite Functional Consultant',
                  'NetSuite Developer',
                  'NetSuite Solution Architect',
                  'NetSuite Implementation Consultant',
                  'NetSuite Technical Consultant',
                  'NetSuite Project Manager',
                  'NetSuite Support Analyst'
                ].map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Briefcase className="w-5 h-5 text-sky-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Clients */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Companies Using NetSuite</p>
          <div className="flex flex-wrap justify-center gap-6">
            {corporateClients.map((client) => (
              <div key={client} className="px-6 py-3 bg-white rounded-lg shadow-sm text-slate-700 font-medium">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-sky-600 to-sky-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master NetSuite ERP?
            </h2>
            <p className="text-lg text-sky-100 mb-8">
              Join our next batch and become a certified NetSuite professional. 
              Customized training available for individuals and corporate teams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
